import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { ApolloService } from '../apollo.service';
import { UsersService } from '../users/users.service';
import * as fromGraphQl from './graphql';

/**
 * Gleicht die bevorzugte Sprache zwischen Backend und localStorage-Cache ab — genau einmal
 * pro Session (#735).
 *
 * Hintergrund: Der Boot-Pfad (keycloak-init.factory) wendet den Cache-Wert sofort an, damit die
 * UI-Sprache steht, bevor die erste Query aufloest. Sobald der Backend-Wert vorliegt, gewinnt er.
 *
 * Bewusst ein eigener, schmaler Service und nicht Teil des UsersService: das Hochschreiben eines
 * Alt-Cache-Werts ist eine einmalige Uebergangsmechanik mit eigener Lebensdauer und soll spaeter
 * ohne Rueckbau an fremdem Code entfernt werden koennen.
 *
 * Angestossen aus LayoutComponent.ngOnInit — der einzigen Komponente hinter dem AuthGuard, die
 * genau einmal pro Session instanziiert wird. Ausdruecklich NICHT aus einem Guard oder Interceptor
 * (die laufen pro Navigation bzw. pro Request) und nicht aus dem Profil (das oeffnet nicht jeder).
 */
@Injectable()
export class PreferredLanguageSyncService {

  private hasSynced = false;

  constructor(
    private apolloService: ApolloService,
    private usersService: UsersService,
    private translateService: TranslateService
  ) {}

  /**
   * Vier Faelle:
   * - Backend-Wert vorhanden und != Cache  -> Backend gewinnt: anwenden + Cache aktualisieren
   * - Backend-Wert vorhanden und == Cache  -> nichts
   * - Backend leer, Cache gefuellt         -> einmalig hochschreiben (Migration Alt-Nutzer)
   * - beide leer                           -> nichts
   *
   * Mehrfachaufrufe innerhalb derselben Session sind wirkungslos.
   */
  public syncOnce(): void {
    if (this.hasSynced) {
      return;
    }
    this.hasSynced = true;

    this.apolloService
      .query<fromGraphQl.CurrentUserLanguageResponse>(fromGraphQl.GET_CURRENT_USER_LANGUAGE_QUERY)
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          const backendLanguage = result?.data?.getCurrentUser?.preferredLanguage;
          const cachedLanguage = this.usersService.getCurrentUsersPreferredlanguage();

          if (backendLanguage) {
            if (backendLanguage !== cachedLanguage) {
              this.applyLanguage(backendLanguage);
            }
            return;
          }

          if (cachedLanguage) {
            this.pushCachedLanguage(cachedLanguage);
          }
        },
        error: (error) => {
          // Der Nutzer hat nichts angefordert — kein Toast, nur loggen.
          console.warn('preferred language sync failed', error);
        }
      });
  }

  private applyLanguage(language: string): void {
    const userId = this.usersService.getCurrentUserId();
    if (userId) {
      localStorage.setItem(userId + '_language', language);
    }
    this.translateService.use(language);
  }

  private pushCachedLanguage(language: string): void {
    this.usersService.updatePreferredLanguage(language).subscribe({
      next: () => {
        // updatePreferredLanguage schreibt Cache und translateService selbst.
      },
      error: (error) => {
        // Migration ist best effort — bei Fehler bleibt der Cache-Wert, kein Toast.
        console.warn('pushing cached preferred language failed', error);
      }
    });
  }
}
